import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useGetCountriesQuery, useGetCitiesQuery, useGetCurrenciesQuery, useGetEducationLevelsQuery, useAddCountryMutation } from '../../store/api';
import { toast } from 'react-toastify';

function UniversityEditModal({
  editingUniversity,
  formData,
  handleSubmit,
  setFormData,
  setIsModalOpen,
  currenciesData,
  educationLevelsData
}) {
  // Country creation modal states
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [newCountryData, setNewCountryData] = useState({ name: '', code: '', is_active: 1 });
  const [countrySearchValue, setCountrySearchValue] = useState('');

  const { data: countriesData, refetch: refetchCountries } = useGetCountriesQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  const { data: citiesData } = useGetCitiesQuery({
    keyword: '',
    country_id: formData.country_id || '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  // Get Currencies for university programs (if not provided as props)
  const { data: currenciesDataFromAPI } = useGetCurrenciesQuery();

  // Get Education Levels for university programs (if not provided as props)
  const { data: educationLevelsDataFromAPI } = useGetEducationLevelsQuery();

  // Add Country Mutation
  const [addCountry, {
    data: countryCreateResponse,
    isError: countryCreateIsError,
    error: countryCreateError,
    isSuccess: countryCreateIsSuccess,
    isLoading: countryCreateIsLoading
  }] = useAddCountryMutation();

  // Extract actual data arrays from API responses or props
  const currencies = currenciesData?.data || currenciesData || currenciesDataFromAPI?.data || currenciesDataFromAPI || [];
  const educationLevels = educationLevelsData?.data || educationLevelsData || educationLevelsDataFromAPI?.data || educationLevelsDataFromAPI || [];

  // Country create useEffect
  useEffect(() => {
    if (!countryCreateIsLoading && countryCreateResponse) {
      toast.success(countryCreateResponse.message);
      setShowCountryModal(false);
      setNewCountryData({ name: '', code: '', is_active: 1 });
      // Refresh countries list and select the new country
      refetchCountries().then((result) => {
        if (result.data) {
          const newCountry = result.data.data.data.find(country => country.name === countryCreateResponse.data.name);
          if (newCountry) {
            setFormData(prev => ({ ...prev, country_id: newCountry.id, city_id: '' }));
          }
        }
      });
    }

    if (countryCreateIsError) {
      toast.error(countryCreateError?.data?.message || 'Ölkə əlavə etmə uğursuz oldu');
    }
  }, [countryCreateResponse, countryCreateIsSuccess, countryCreateIsError, countryCreateIsLoading, countryCreateError, refetchCountries, setFormData]);

  // Country handlers
  const handleCreateCountry = () => {
    setNewCountryData({ name: countrySearchValue, code: '', is_active: 1 });
    setShowCountryModal(true);
  };

  const handleCountrySubmit = (e) => {
    e.preventDefault();
    addCountry(newCountryData);
  };

  const handleCancelCountryModal = () => {
    setShowCountryModal(false);
    setNewCountryData({ name: '', code: '', is_active: 1 });
  };

  // Prepare country options for React Select
  const countryOptions = countriesData?.data?.data?.map(country => ({
    value: country.id.toString(),
    label: country.name
  })) || [];

  const addProgram = () => {
    setFormData({
      ...formData,
      programs: [...(formData.programs || []), { 
        name: '', 
        degree: [], 
        duration: '', 
        tuition_amount: '', 
        currency_id: '',
        description: '',
        is_active: true
      }]
    });
  };

  const removeProgram = (index) => {
    const newPrograms = (formData.programs || []).filter((_, i) => i !== index);
    setFormData({ ...formData, programs: newPrograms });
  };

  const updateProgram = (index, field, value) => {
    const newPrograms = (formData.programs || []).map((program, i) => 
      i === index ? { ...program, [field]: value } : program
    );
    setFormData({ ...formData, programs: newPrograms });
  };

  // Scholarship functions
  const addScholarship = () => {
    setFormData({
      ...formData,
      scholarships: [...(formData.scholarships || []), { 
        name: '',
        description: '',
        degree_level: [],
        field_of_study: '',
        amount: '',
        currency_id: '',
        amount_type: '',
        coverage_type: '',
        application_deadline: '',
        requirements: '',
        application_process: '',
        contact_email: '',
        website: '',
        additional_benefits: '',
        duration: '',
        is_active: true
      }]
    });
  };

  const removeScholarship = (index) => {
    const newScholarships = (formData.scholarships || []).filter((_, i) => i !== index);
    setFormData({ ...formData, scholarships: newScholarships });
  };

  const updateScholarship = (index, field, value) => {
    const newScholarships = (formData.scholarships || []).map((scholarship, i) => 
      i === index ? { ...scholarship, [field]: value } : scholarship
    );
    setFormData({ ...formData, scholarships: newScholarships });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
        
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {editingUniversity ? 'Universiteti Redaktə Et' : 'Yeni Universitet Əlavə Et'}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {editingUniversity ? 'Universitet məlumatlarını yeniləyin' : 'Yeni universitet məlumatlarını daxil edin'}
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Section 1: Basic Information */}
            <div className="bg-white border border-blue-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200 rounded-t-xl">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-blue-900">Əsas Məlumatlar</h4>
                </div>
                <p className="mt-1 text-sm text-blue-700">Universitətin əsas məlumatları və yeri</p>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Universitet Adı
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Məsələn: Harvard University"
                    />
                    <p className="mt-1 text-xs text-gray-500">Universitətin tam rəsmi adı</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ölkə
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <Select
                      value={formData.country_id ? 
                        countryOptions.find(option => option.value === formData.country_id.toString()) : null
                      }
                      onChange={(selectedOption) => {
                        setFormData({ ...formData, country_id: selectedOption?.value || '', city_id: '' });
                      }}
                      onInputChange={(inputValue) => {
                        setCountrySearchValue(inputValue);
                      }}
                      options={countryOptions}
                      placeholder="Ölkə axtarın və ya seçin..."
                      isSearchable={true}
                      isClearable={true}
                      noOptionsMessage={({ inputValue }) => (
                        <div className="p-2">
                          <div className="text-gray-500 mb-2">"{inputValue}" tapılmadı</div>
                          <button
                            type="button"
                            onClick={handleCreateCountry}
                            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                          >
                            + "{inputValue}" ölkəsini yarat
                          </button>
                        </div>
                      )}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                          boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                          padding: '8px 4px',
                          '&:hover': {
                            borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                          }
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected 
                            ? '#3b82f6' 
                            : state.isFocused 
                            ? '#dbeafe' 
                            : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#3b82f6' : '#dbeafe'
                          }
                        }),
                        noOptionsMessage: (provided) => ({
                          ...provided,
                          padding: 0,
                        })
                      }}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Şəhər
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <select
                      required
                      value={formData.city_id}
                      onChange={(e) => setFormData({ ...formData, city_id: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={!formData.country_id}
                    >
                      <option value="">Şəhər seçin...</option>
                      {citiesData?.data?.data?.map(city => (
                        <option key={city.id} value={city.id}>{city.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reytinq
                    </label>
                    <input
                      type="number"
                      value={formData.ranking}
                      onChange={(e) => setFormData({ ...formData, ranking: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Məsələn: 1"
                    />
                    <p className="mt-1 text-xs text-gray-500">Dünya reytinqində yeri</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active === 1}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Universitet aktiv statusdadır
                      </span>
                      <p className="text-xs text-gray-500">
                        Deaktiv universitetlər müraciət seçimlərində göstərilməz
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Contact & Representatives */}
            <div className="bg-white border border-green-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200 rounded-t-xl">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-green-600 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-green-900">Əlaqə Məlumatları</h4>
                </div>
                <p className="mt-1 text-sm text-green-700">Nümayəndələr və əlaqə məlumatları</p>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nümayəndələr
                    </label>
                    <input
                      type="text"
                      value={formData.representatives || ''}
                      onChange={(e) => setFormData({ ...formData, representatives: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Məsələn: Dr. John Smith, Prof. Jane Doe"
                    />
                    <p className="mt-1 text-xs text-gray-500">Universitətin əlaqə nümayəndələri</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Focal Point
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.focal_point}
                      onChange={(e) => setFormData({ ...formData, focal_point: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Məsələn: John Doe"
                    />
                    <p className="mt-1 text-xs text-gray-500">Əsas əlaqə şəxsi</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Məsələn: international@university.edu"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Məsələn: +1 617 495 1000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Vebsayt
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Məsələn: https://www.university.edu"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Academic Information */}
            <div className="bg-white border border-purple-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-purple-200 rounded-t-xl">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-purple-600 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-purple-900">Akademik Məlumatlar</h4>
                </div>
                <p className="mt-1 text-sm text-purple-700">Təhsil dərəcələri və akademik şərtlər</p>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Təklif Olunan Dərəcələr
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="space-y-3">
                      {['Bakalavr', 'Magistr', 'Doktorantura'].map((degree) => (
                        <label key={degree} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.degree && Array.isArray(formData.degree) && formData.degree.includes(degree)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ 
                                  ...formData, 
                                  degree: [...(formData.degree || []).filter(d => d !== degree), degree] 
                                });
                              } else {
                                setFormData({ 
                                  ...formData, 
                                  degree: (formData.degree || []).filter(d => d !== degree) 
                                });
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-gray-700">{degree}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Müraciət Son Tarixi
                      </label>
                      <input
                        type="text"
                        value={formData.submission_deadline}
                        onChange={(e) => setFormData({ ...formData, submission_deadline: e.target.value })}
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Məsələn: 15 Mart 2024"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Orta İllik Qiymət
                      </label>
                      <input
                        type="text"
                        value={formData.average_cost || ''}
                        onChange={(e) => setFormData({ ...formData, average_cost: e.target.value })}
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Məsələn: 8000-12000 EUR/il"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ünvan
                    </label>
                    <textarea
                      rows={3}
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                      placeholder="Məsələn: 123 University Street, Cambridge, MA 02138, USA"
                    />
                    <p className="mt-1 text-xs text-gray-500">Universitətin fiziki ünvanı</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Documents & Additional Information */}
            <div className="bg-white border border-orange-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b border-orange-200 rounded-t-xl">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-orange-600 rounded-lg flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-orange-900">Sənədlər və Əlavə Məlumatlar</h4>
                </div>
                <p className="mt-1 text-sm text-orange-700">Tələb olunan sənədlər və qeydlər</p>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tələb Olunan Sənədlər
                  </label>
                  <textarea
                    rows={4}
                    value={formData.required_documents || ''}
                    onChange={(e) => setFormData({ ...formData, required_documents: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                    placeholder="Məsələn: Diplom, Transkript, Motivasiya məktubu, CV, İngilis dili sertifikatı, Referans məktubları"
                  />
                  <p className="mt-1 text-xs text-gray-500">Müraciət üçün tələb olunan bütün sənədləri sadalayın</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Əlavə Şərhlər və Qeydlər
                  </label>
                  <textarea
                    rows={4}
                    value={formData.comments || ''}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                    placeholder="Məsələn: Minimum IELTS tələbi, xüsusi şərtlər, təqaüd imkanları və s."
                  />
                  <p className="mt-1 text-xs text-gray-500">Müraciətçilər üçün faydalı əlavə məlumatlar</p>
                </div>
              </div>
            </div>

            {/* Section 5: University Programs */}
            <div className="bg-white border border-indigo-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 px-6 py-4 border-b border-indigo-200 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-indigo-900">Ixtisaslar</h4>
                      <p className="text-sm text-indigo-700">Təklif olunan ixtisaslar</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addProgram}
                    className="inline-flex items-center px-3 py-2 border border-indigo-300 rounded-md text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Ixtisas əlavə et
                  </button>
                </div>
              </div>
              <div className="p-6">
                {formData.programs && formData.programs.length > 0 ? (
                  <div className="space-y-4">
                    {formData.programs.map((program, index) => (
                      <div key={index} className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-sm font-medium text-indigo-900">Ixtisas #{index + 1}</h5>
                          <button
                            type="button"
                            onClick={() => removeProgram(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Ixtisasi sil"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Ixtisas Adı *
                            </label>
                            <input
                              type="text"
                              required
                              value={program.name}
                              onChange={(e) => updateProgram(index, 'name', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Məsələn: Computer Science"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Dərəcə *
                            </label>
                            <Select
                              value={educationLevels && program.degree && Array.isArray(program.degree) ? 
                                program.degree.map(levelId => {
                                  const level = educationLevels.find(l => l.id && l.id.toString() === levelId.toString());
                                  return level ? { value: level.id.toString(), label: level.name } : null;
                                }).filter(Boolean) : []
                              }
                              onChange={(selectedOptions) => {
                                const selectedIds = selectedOptions && Array.isArray(selectedOptions) ? 
                                  selectedOptions.map(option => option.value) : [];
                                updateProgram(index, 'degree', selectedIds);
                              }}
                              options={educationLevels && Array.isArray(educationLevels) ? 
                                educationLevels.map(level => ({
                                  value: level.id ? level.id.toString() : '',
                                  label: level.name || ''
                                })) : []}
                              placeholder="Dərəcə seçin..."
                              isSearchable={true}
                              isMulti={true}
                              isClearable={true}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
                                  boxShadow: state.isFocused ? '0 0 0 1px #4f46e5' : 'none',
                                  fontSize: '14px',
                                  '&:hover': {
                                    borderColor: state.isFocused ? '#4f46e5' : '#9ca3af'
                                  }
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected 
                                    ? '#4f46e5' 
                                    : state.isFocused 
                                    ? '#e0e7ff' 
                                    : 'white',
                                  color: state.isSelected ? 'white' : '#374151',
                                  fontSize: '14px'
                                })
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Müddət
                            </label>
                            <input
                              type="text"
                              value={program.duration}
                              onChange={(e) => updateProgram(index, 'duration', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Məsələn: 2 il"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Təhsil Haqqı
                            </label>
                            <input
                              type="number"
                              value={program.tuition_amount}
                              onChange={(e) => updateProgram(index, 'tuition_amount', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Məsələn: 8000"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Valyuta *
                            </label>
                            <Select
                              value={currencies && program.currency_id ? 
                                (() => {
                                  const foundCurrency = currencies.find(curr => curr.id && curr.id.toString() === program.currency_id.toString());
                                  return foundCurrency ? 
                                    { value: foundCurrency.id.toString(), label: `${foundCurrency.code} - ${foundCurrency.name}` } : 
                                    null;
                                })()
                                : null}
                              onChange={(selectedOption) => {
                                updateProgram(index, 'currency_id', selectedOption?.value || '');
                              }}
                              options={currencies ? currencies.map(currency => ({
                                value: currency.id.toString(),
                                label: `${currency.code} - ${currency.name}`
                              })) : []}
                              placeholder="Valyuta seçin..."
                              isSearchable={true}
                              isClearable={true}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
                                  boxShadow: state.isFocused ? '0 0 0 1px #4f46e5' : 'none',
                                  fontSize: '14px',
                                  '&:hover': {
                                    borderColor: state.isFocused ? '#4f46e5' : '#9ca3af'
                                  }
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected 
                                    ? '#4f46e5' 
                                    : state.isFocused 
                                    ? '#e0e7ff' 
                                    : 'white',
                                  color: state.isSelected ? 'white' : '#374151',
                                  fontSize: '14px',
                                  '&:hover': {
                                    backgroundColor: state.isSelected ? '#4f46e5' : '#e0e7ff'
                                  }
                                })
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>
                          
                          <div className="flex items-center">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={program.is_active}
                                onChange={(e) => updateProgram(index, 'is_active', e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-xs font-medium text-gray-700">Aktiv</span>
                            </label>
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Təsvir
                          </label>
                          <textarea
                            rows={2}
                            value={program.description}
                            onChange={(e) => updateProgram(index, 'description', e.target.value)}
                            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                            placeholder="Ixtisas haqqında qısa məlumat..."
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="mx-auto h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="h-6 w-6 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Ixtisas əlavə edilməyib</h5>
                    <p className="text-xs text-gray-500 mb-4">
                      Universitətin təklif etdiyi ixtisaslari əlavə edin
                    </p>
                    <button
                      type="button"
                      onClick={addProgram}
                      className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      İlk Ixtisasi Əlavə Et
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Section 6: Scholarship Programs */}
            <div className="bg-white border border-emerald-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 px-6 py-4 border-b border-emerald-200 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-emerald-900">Təqaüd Proqramları</h4>
                      <p className="text-sm text-emerald-700">Təklif olunan təqaüd proqramları</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addScholarship}
                    className="inline-flex items-center px-3 py-2 border border-emerald-300 rounded-md text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Təqaüd əlavə et
                  </button>
                </div>
              </div>
              <div className="p-6">
                {formData.scholarships && formData.scholarships.length > 0 ? (
                  <div className="space-y-6">
                    {formData.scholarships.map((scholarship, index) => (
                      <div key={index} className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-sm font-medium text-emerald-900">Təqaüd #{index + 1}</h5>
                          <button
                            type="button"
                            onClick={() => removeScholarship(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Təqaüdü sil"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>

                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Təqaüd Adı *
                            </label>
                            <input
                              type="text"
                              required
                              value={scholarship.name}
                              onChange={(e) => updateScholarship(index, 'name', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Məsələn: DAAD Scholarship"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Sahə *
                            </label>
                            <input
                              type="text"
                              required
                              value={scholarship.field_of_study}
                              onChange={(e) => updateScholarship(index, 'field_of_study', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Məsələn: Mühendislik, İT"
                            />
                          </div>
                        </div>

                        {/* Degree Level and Financial Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Dərəcə Səviyyəsi *
                            </label>
                            <Select
                              value={educationLevels && scholarship.degree_level && Array.isArray(scholarship.degree_level) ? 
                                scholarship.degree_level.map(levelId => {
                                  const level = educationLevels.find(l => l.id && l.id.toString() === levelId.toString());
                                  return level ? { value: level.id.toString(), label: level.name } : null;
                                }).filter(Boolean) : []
                              }
                              onChange={(selectedOptions) => {
                                const selectedIds = selectedOptions && Array.isArray(selectedOptions) ? 
                                  selectedOptions.map(option => option.value) : [];
                                updateScholarship(index, 'degree_level', selectedIds);
                              }}
                              options={educationLevels && Array.isArray(educationLevels) ? 
                                educationLevels.map(level => ({
                                  value: level.id ? level.id.toString() : '',
                                  label: level.name || ''
                                })) : []}
                              placeholder="Dərəcə seçin..."
                              isSearchable={true}
                              isMulti={true}
                              isClearable={true}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: state.isFocused ? '#059669' : '#d1d5db',
                                  boxShadow: state.isFocused ? '0 0 0 1px #059669' : 'none',
                                  fontSize: '14px',
                                  '&:hover': {
                                    borderColor: state.isFocused ? '#059669' : '#9ca3af'
                                  }
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected 
                                    ? '#059669' 
                                    : state.isFocused 
                                    ? '#d1fae5' 
                                    : 'white',
                                  color: state.isSelected ? 'white' : '#374151',
                                  fontSize: '14px'
                                })
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Məbləğ *
                            </label>
                            <input
                              type="number"
                              required
                              value={scholarship.amount}
                              onChange={(e) => updateScholarship(index, 'amount', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="25000"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Valyuta *
                            </label>
                            <Select
                              value={currencies && scholarship.currency_id ? 
                                (() => {
                                  const foundCurrency = currencies.find(curr => curr.id && curr.id.toString() === scholarship.currency_id.toString());
                                  return foundCurrency ? 
                                    { value: foundCurrency.id.toString(), label: `${foundCurrency.code} - ${foundCurrency.name}` } : 
                                    null;
                                })()
                                : null}
                              onChange={(selectedOption) => {
                                updateScholarship(index, 'currency_id', selectedOption?.value || '');
                              }}
                              options={currencies ? currencies.map(currency => ({
                                value: currency.id.toString(),
                                label: `${currency.code} - ${currency.name}`
                              })) : []}
                              placeholder="Valyuta seçin..."
                              isSearchable={true}
                              isClearable={true}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: state.isFocused ? '#059669' : '#d1d5db',
                                  boxShadow: state.isFocused ? '0 0 0 1px #059669' : 'none',
                                  fontSize: '14px'
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected 
                                    ? '#059669' 
                                    : state.isFocused 
                                    ? '#d1fae5' 
                                    : 'white',
                                  color: state.isSelected ? 'white' : '#374151',
                                  fontSize: '14px'
                                })
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Ödəniş Növü *
                            </label>
                            <Select
                              value={scholarship.amount_type ? {
                                value: scholarship.amount_type,
                                label: scholarship.amount_type
                              } : null}
                              onChange={(selectedOption) => {
                                updateScholarship(index, 'amount_type', selectedOption?.value || '');
                              }}
                              options={[
                                { value: 'Aylıq', label: 'Aylıq' },
                                { value: 'İllik', label: 'İllik' },
                                { value: 'Birdəfəlik', label: 'Birdəfəlik' }
                              ]}
                              placeholder="Ödəniş növü..."
                              isSearchable={true}
                              isClearable={true}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: state.isFocused ? '#059669' : '#d1d5db',
                                  boxShadow: state.isFocused ? '0 0 0 1px #059669' : 'none',
                                  fontSize: '14px'
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected 
                                    ? '#059669' 
                                    : state.isFocused 
                                    ? '#d1fae5' 
                                    : 'white',
                                  color: state.isSelected ? 'white' : '#374151',
                                  fontSize: '14px'
                                })
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>
                        </div>

                        {/* Coverage Type, Duration and Deadline */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Əhatə Növü *
                            </label>
                            <Select
                              value={scholarship.coverage_type ? {
                                value: scholarship.coverage_type,
                                label: scholarship.coverage_type
                              } : null}
                              onChange={(selectedOption) => {
                                updateScholarship(index, 'coverage_type', selectedOption?.value || '');
                              }}
                              options={[
                                { value: 'Tam təqaüd', label: 'Tam təqaüd' },
                                { value: 'Qismən təqaüd', label: 'Qismən təqaüd' }
                              ]}
                              placeholder="Əhatə növü..."
                              isSearchable={true}
                              isClearable={true}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: state.isFocused ? '#059669' : '#d1d5db',
                                  boxShadow: state.isFocused ? '0 0 0 1px #059669' : 'none',
                                  fontSize: '14px'
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected 
                                    ? '#059669' 
                                    : state.isFocused 
                                    ? '#d1fae5' 
                                    : 'white',
                                  color: state.isSelected ? 'white' : '#374151',
                                  fontSize: '14px'
                                })
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Müddət *
                            </label>
                            <input
                              type="text"
                              required
                              value={scholarship.duration}
                              onChange={(e) => updateScholarship(index, 'duration', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="12 ay, 24 ay"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Son Müraciət Tarixi *
                            </label>
                            <input
                              type="date"
                              required
                              value={scholarship.application_deadline}
                              onChange={(e) => updateScholarship(index, 'application_deadline', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          </div>
                        </div>

                        {/* Contact Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Email *
                            </label>
                            <input
                              type="email"
                              required
                              value={scholarship.contact_email}
                              onChange={(e) => updateScholarship(index, 'contact_email', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="contact@scholarship.org"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Vebsayt *
                            </label>
                            <input
                              type="url"
                              required
                              value={scholarship.website}
                              onChange={(e) => updateScholarship(index, 'website', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="https://www.scholarship.org"
                            />
                          </div>
                        </div>

                        {/* Description and Requirements */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Təqaüd Təsviri *
                            </label>
                            <textarea
                              rows={2}
                              required
                              value={scholarship.description}
                              onChange={(e) => updateScholarship(index, 'description', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                              placeholder="Təqaüd proqramı haqqında qısa məlumat..."
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Tələb Olunan Sənədlər *
                            </label>
                            <textarea
                              rows={2}
                              required
                              value={scholarship.requirements}
                              onChange={(e) => updateScholarship(index, 'requirements', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                              placeholder="IELTS, GPA, CV, Motivasiya məktubu və s."
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Müraciət Prosesi *
                            </label>
                            <textarea
                              rows={2}
                              required
                              value={scholarship.application_process}
                              onChange={(e) => updateScholarship(index, 'application_process', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                              placeholder="Online müraciət prosesi haqqında məlumat..."
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Əlavə Faydalar
                            </label>
                            <input
                              type="text"
                              value={scholarship.additional_benefits}
                              onChange={(e) => updateScholarship(index, 'additional_benefits', e.target.value)}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Yaşayış, səyahət, sığorta və s."
                            />
                          </div>

                          <div className="flex items-center">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={scholarship.is_active}
                                onChange={(e) => updateScholarship(index, 'is_active', e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="text-xs font-medium text-gray-700">Aktiv</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="mx-auto h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Təqaüd əlavə edilməyib</h5>
                    <p className="text-xs text-gray-500 mb-4">
                      Universitətin təklif etdiyi təqaüd proqramlarını əlavə edin
                    </p>
                    <button
                      type="button"
                      onClick={addScholarship}
                      className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      İlk Təqaüdü Əlavə Et
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Ləğv et
              </button>
              <button
                type="submit"
                className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
              >
                {editingUniversity ? '✓ Yenilə' : '✓ Yadda saxla'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Country Creation Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleCancelCountryModal} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Yeni Ölkə Əlavə Et
                  </h3>
                  <button
                    onClick={handleCancelCountryModal}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-xl">&times;</span>
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Sistemdə olmayan yeni ölkə əlavə edin
                </p>
              </div>

              <form onSubmit={handleCountrySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ölkə Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={newCountryData.name}
                    onChange={(e) => setNewCountryData({ ...newCountryData, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Məsələn: Almanya"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ölkə Kodu *
                  </label>
                  <input
                    type="text"
                    required
                    value={newCountryData.code}
                    onChange={(e) => setNewCountryData({ ...newCountryData, code: e.target.value.toUpperCase() })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Məsələn: DE"
                    maxLength="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="mt-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={newCountryData.is_active}
                        onChange={(e) => setNewCountryData({ ...newCountryData, is_active: e.target.checked ? 1 : 0 })}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Ölkə aktiv statusdadır
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelCountryModal}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    disabled={countryCreateIsLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {countryCreateIsLoading ? 'Əlavə edilir...' : 'Yadda saxla'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UniversityEditModal; 